'use client';

import Image from 'next/image';
import React from 'react';
import { IconArrowRight } from '@icons';
import { Button, Card } from '@ui';
import { portfolioPublicPath } from '@lib/portfolioBreadcrumbs';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import type { CarouselItem } from '../carousels/CarouselData';
import ImageCardModal from './ImageCardModal';

const DEFAULT_IMAGE_URL = '/images/app-icons/logo.svg';

export interface PortfolioImageCardProps extends React.HTMLAttributes<HTMLDivElement> {
  'carousel-item'?: CarouselItem;
  'carousel-index'?: number;
  locale?: 'en' | 'es';
}

const PortfolioImageCard: React.FC<PortfolioImageCardProps> = ({
  'carousel-item': carouselItem,
  'carousel-index': _carouselIndex,
  className,
  style,
  locale = 'en',
  ...rest
}) => {
  const viewImageLabel = String(
    renderTranslation(t('actions.view_image', 'global', locale)) || 'View Image'
  );
  const viewSiteLabel = String(
    renderTranslation(t('actions.view_site', 'global', locale)) || 'Visit Site'
  );
  const viewMoreDetailsLabel = String(
    renderTranslation(t('actions.view_more_details', 'global', locale)) || 'View More Details'
  );

  const label = carouselItem?.title ?? 'Untitled';
  const summary = carouselItem?.label ?? '';
  const metaLine = typeof carouselItem?.meta === 'string' ? carouselItem.meta.trim() : '';
  const rawSrc1x = carouselItem?.image_url?.trim();
  const rawSrc2x = carouselItem?.image_url_2x?.trim();
  const effective1x = rawSrc1x || DEFAULT_IMAGE_URL;
  const isDefaultImage = !rawSrc1x;
  const hasOverlay = Boolean(carouselItem?.hasOverlay);
  const overlayDescription = carouselItem?.description_long?.trim() || summary;
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const hasPageSlug = Boolean(carouselItem?.hasPageSlug);
  const pageSlug = carouselItem?.pageSlug?.trim();
  const sectionSlug = carouselItem?.sectionSlug?.trim();
  const [use2x, setUse2x] = React.useState(false);

  React.useEffect(() => {
    if (!rawSrc2x) return;
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
    setUse2x(dpr >= 2);
  }, [rawSrc2x]);

  const rawSrc = use2x && rawSrc2x ? rawSrc2x : effective1x;
  const href = carouselItem?.url?.trim();
  const target = carouselItem?.target?.trim();
  const rel = carouselItem?.rel?.trim() || (target === '_blank' ? 'noreferrer' : undefined);

  const handleOverlayActivate = React.useCallback(() => {
    if (href) {
      if (target === '_blank') {
        const features = rel?.includes('noreferrer') ? 'noopener,noreferrer' : 'noopener';
        window.open(href, '_blank', features);
        return;
      }
      window.location.assign(href);
      return;
    }
    setIsOverlayOpen(true);
  }, [href, rel, target]);

  const detailHref =
    hasPageSlug && pageSlug && sectionSlug
      ? portfolioPublicPath(locale, sectionSlug, pageSlug)
      : null;

  const mediaBlock = (
    <div
      className={['image-card-media', isDefaultImage ? 'is-default-image' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <Image
        src={rawSrc}
        alt={label}
        fill
        sizes='(max-width: 768px) 100vw, 220px'
        style={{ objectFit: isDefaultImage ? 'contain' : 'cover' }}
        unoptimized={rawSrc.toLowerCase().endsWith('.svg')}
      />
    </div>
  );

  const mediaSection = hasOverlay ? (
    <div
      className='image-overlay-trigger'
      role='button'
      tabIndex={0}
      aria-label={viewImageLabel}
      onClick={handleOverlayActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOverlayActivate();
        }
      }}
    >
      {mediaBlock}
      <div className='image-content-actions'>
        <span className='btn btn-primary'>{viewImageLabel}</span>
      </div>
    </div>
  ) : (
    mediaBlock
  );

  return (
    <Card
      {...rest}
      className={['image-card', 'portfolio-image-card', hasOverlay ? 'has-overlay' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      <div className='image-container'>
        {mediaSection}
        <div className='portfolio-image-card-copy'>
          <h3 className='title'>{label}</h3>
          {metaLine ? <p className='meta'>{metaLine}</p> : null}
          {summary ? <p className='summary'>{summary}</p> : null}
          {detailHref || href ? (
            <div className='portfolio-image-card-actions'>
              {detailHref ? (
                <Button
                  as='a'
                  href={detailHref}
                  target='_self'
                  className='btn-link muted'
                  label={viewMoreDetailsLabel}
                  icon={<IconArrowRight />}
                  iconPosition='right'
                />
              ) : null}
              {href ? (
                <Button
                  as='a'
                  href={href}
                  target={target || undefined}
                  rel={rel}
                  className='btn-primary'
                  label={viewSiteLabel}
                  icon={<IconArrowRight />}
                  iconPosition='right'
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <ImageCardModal
        open={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        title={label}
        rawSrc={rawSrc}
        overlayDescription={overlayDescription}
      />
    </Card>
  );
};

export default PortfolioImageCard;
